import React, { useState } from 'react';
import { BookOpenIcon, FileSpreadsheetIcon, HouseIcon, LayoutDashboardIcon } from 'lucide-react';
import { howSteps } from '../data/howItWorks';
import { TL_UNIT_COUNT_REVIEW_NOTE } from '../data/periodClose';
import { BONUS_CAP_NOTE, BONUS_MAIN_NOTE, TIER_LABEL_NOTE, monthlyBonusBands, statementMeta, troyBaseBands } from '../data/statement';
import { RiskNote } from '../components/sf/RiskNote';
import { Button } from '../components/sf/Button';
import { ExampleDataBadge } from '../components/sf/ExampleDataBadge';
import { RecordHeader } from '../components/sf/RecordHeader';
import { AdjustedEquation } from '../components/howItWorks/AdjustedEquation';
import { BonusComparison } from '../components/howItWorks/BonusComparison';
import { PaidTotalCallout } from '../components/howItWorks/PaidTotalCallout';
import { CadenceLegend } from '../components/howItWorks/CadenceLegend';
import { FlowRail } from '../components/howItWorks/FlowRail';
import { LinesVisual } from '../components/howItWorks/LinesVisual';
import { LockVisual } from '../components/howItWorks/LockVisual';
import { PrecedenceChain } from '../components/howItWorks/PrecedenceChain';
import { RollupVisual } from '../components/howItWorks/RollupVisual';
import { StepCard } from '../components/howItWorks/StepCard';
import { BandBar } from '../components/statement/BandBar';
import { ScreenId } from '../types/navigation';
interface HowCommissionsWorkProps {
  onNavigate: (screen: ScreenId) => void;
}
export function HowCommissionsWork({
  onNavigate
}: HowCommissionsWorkProps) {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(['funds']));
  const {
    volume,
    units
  } = statementMeta;
  const toggle = (id: string) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);else next.add(id);
    return next;
  });
  const jumpTo = (id: string) => {
    setExpanded(prev => new Set(prev).add(id));
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    document.getElementById(`step-${id}`)?.scrollIntoView({
      behavior: reduceMotion ? 'auto' : 'smooth',
      block: 'start'
    });
  };
  const visuals: Record<string, React.ReactNode> = {
    plan: <div className="space-y-2" data-id="element-1120" data-mp="2ozeegn2rph0w">
        <PrecedenceChain data-id="element-1121" data-mp="2u13uyi8remwd" />
        <p className="text-xs text-sf-weak" data-id="element-1122" data-mp="2tzalkv6rhoko">Each commission line stores a snapshot of the plan version and inputs used, so it can always be explained later.</p>
      </div>,
    base: <div className="space-y-2" data-id="element-1123" data-mp="21cy89py5antw">
        <BandBar bands={troyBaseBands} value={volume / 1000000} scaleMax={3} markerLabel="Alex · Troy · $2,485,400.00" ariaLabel="Troy period volume $2,485,400.00 places Alex at 125 bps" data-id="element-1124" data-mp="21c6bibstak21" />
        <p className="text-xs text-sf-weak" data-id="element-1125" data-mp="1dmvl9o938825">Miami: 100, or 150 bps at ≥ $2,000,000.00 · Arizona: flat 100 bps.</p>
        <RiskNote data-id="element-1126" data-mp="1ikdiihdqsyub" />
        <p className="text-[11px] text-sf-weak" data-id="element-1127" data-mp="1ijubhvpmruv2">{TIER_LABEL_NOTE}</p>
      </div>,
    bonus: <div className="space-y-4" data-id="element-1128" data-mp="1b1e12h9udhdl">
        <BandBar bands={monthlyBonusBands} value={units} scaleMax={9} discrete markerLabel={`Alex · ${units} closed loans`} ariaLabel={`Alex · ${units} closed loans in October gives a +50 bps bonus`} data-id="element-1129" data-mp="1azjn5mwzmpj0" />
        <p className="text-xs text-sf-weak" data-id="element-1130" data-mp="x1lgz4stlzel">
          7 closed loans → +50, capped to +25: {BONUS_CAP_NOTE} <span className="text-[11px]" data-id="element-1131" data-mp="s4ctcoowl35y">{BONUS_MAIN_NOTE}</span>
        </p>
        <BonusComparison data-id="element-1132" data-mp="nba771mggvzs" />
        <p className="rounded border border-[#F9C88C] bg-sf-amber-light px-3 py-2 text-xs text-sf-text" data-id="element-1133" data-mp="nb0jjkovv0x9">
          {TL_UNIT_COUNT_REVIEW_NOTE}
        </p>
      </div>,
    adjusted: <AdjustedEquation data-id="element-1134" data-mp="dfglosqrds5v" />,
    lines: <LinesVisual data-id="element-1135" data-mp="dc2nzxt1i74r" />,
    rollup: <RollupVisual data-id="element-1136" data-mp="9cahrrr4cd2k" />,
    lock: <LockVisual data-id="element-1137" data-mp="9biowoowotfz" />
  };
  return <div className="space-y-3" data-id="element-1138" data-mp="9emyy2p1yjek">
      <RecordHeader objectLabel="Guide" title="How Commissions Work" icon={BookOpenIcon} iconBg="#032D60" titleAddon={<ExampleDataBadge data-id="element-1140" data-mp="24g8j37zhwb4v" />} actions={<>
            <Button icon={<FileSpreadsheetIcon data-id="element-1142" data-mp="28g9275xjhai9" />} onClick={() => onNavigate('plans')} data-id="element-1141" data-mp="28hk4107fc86q">
              See the rules
            </Button>
            <Button icon={<HouseIcon data-id="element-1144" data-mp="1unt32g7rdwmm" />} onClick={() => onNavigate('loan')} data-id="element-1143" data-mp="1upnen3x54ce1">
              See this on the loan
            </Button>
            <Button icon={<LayoutDashboardIcon data-id="element-1146" data-mp="1kr7y1uvqolis" />} onClick={() => onNavigate('statement')} data-id="element-1145" data-mp="1krprkjr0oln0">
              See October statement
            </Button>
          </>} toolbar={<div className="space-y-1" data-id="element-1147" data-mp="1pog8186qhj8y">
            <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[14px]" data-id="element-1148" data-mp="1brtb35es6t31">
              <span data-id="element-1149" data-mp="1gpmcll552ywz">
                <strong data-id="element-1150" data-mp="1gr5shamvv7y8">Adjusted (pays)</strong> = (<span className="text-sf-purple" data-id="element-1151" data-mp="1gs8jrjo4bceo">Base BPS</span> + <span className="text-sf-blue-dark" data-id="element-1152" data-mp="364stt7egk3ej">Monthly bonus BPS</span>) ×{' '}
                <span className="text-sf-green-text" data-id="element-1153" data-mp="3b3vobxwtar89">Adjusted Loan Amount</span>
              </span>
            </p>
            <p className="text-[13px] tabular-nums" data-id="element-1154" data-mp="3l7asqlpd5jtt">
              Alex Rivera, LN-2026-004812: (125 + 25) bps × $385,692.31 = <strong data-id="element-1155" data-mp="3ga2akl47tfsk">$5,785.38</strong> adjusted
            </p>
            <p className="text-[11px] tabular-nums text-sf-weak" data-id="element-1156" data-mp="3u05omosxy36x">
              Stored (unadjusted) – reference: same BPS × Loan Amount · (125 + 25) bps × $412,500.00 = $6,187.50
            </p>
            <div className="pt-1.5" data-id="element-1157" data-mp="7t7hfxd7z9ro">
              <PaidTotalCallout data-id="element-1158" data-mp="7s4lvc4ycj5f" />
            </div>
          </div>} data-id="element-1139" data-mp="4dplg3ezmdje" />

      <FlowRail steps={howSteps} onJump={jumpTo} data-id="element-1159" data-mp="2uw4hdrmoj58" />

      <ol className="list-none" data-id="element-1160" data-mp="2w76p95fdiu8">
        {howSteps.map((step, index) => <StepCard key={step.id} step={step} expanded={expanded.has(step.id)} isLast={index === howSteps.length - 1} onToggle={() => toggle(step.id)} onNavigate={onNavigate} data-id="element-1161" data-mp="gtcb74phq9nd" data-mpi={index}>
            {visuals[step.id]}
          </StepCard>)}
      </ol>

      <CadenceLegend data-id="element-1162" data-mp="2em0527tw7c2y" />
    </div>;
}